/* eslint-disable react-hooks/exhaustive-deps */

import { Badge, Box, Button, Flex, Stack, Tabs, Text } from '@chakra-ui/react';
import { ListPlus, Microphone } from '@phosphor-icons/react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { fetchAPI } from '@/api/fetch';
import { apiGetAccountProfile } from '@/api/services';
import { AgencyUsage } from '@/types/AgencyUsage';
import { AgencyPlan, AgencyUsageType } from '@/types/enums';

import AgentIndex from './Agent/AgentIndex';
import CopilotIndex from './Copilot/CopilotIndex';
import AddInterviewDialog from './Copilot/AddInterviewDialog';
import AddMockInterviewDialog from './Copilot/AddMockInterviewDialog';
import { LimitExhaustedDialog } from './Agent/LimitExhaustedDialog';

export default function InterviewIndex() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState<string>(searchParams.get('tab') || 'automated');
  const [agencyPlan, setAgencyPlan] = useState(AgencyPlan.FREE);
  const [usage, setUsage] = useState<AgencyUsage | null>(null);
  const [copilotRefreshKey, setCopilotRefreshKey] = useState(0);
  const [automatedUnreadCount, setAutomatedUnreadCount] = useState(0);

  const handleAddMeeting = useCallback(async () => {
    setCopilotRefreshKey((prev) => prev + 1);
  }, []);

  const handleAddMockMeeting = useCallback(async () => {
    setCopilotRefreshKey((prev) => prev + 1);
  }, []);

  const fetchAccount = useCallback(async () => {
    try {
      const response = await fetchAPI<Account>({
        request: () => apiGetAccountProfile()
      });

      if (response.success) {
        const agency = response.data.agency;
        setAgencyPlan(agency.plan);
        setUsage(agency.usages.find((u) => u.type === AgencyUsageType.MeetingTime) || null);
      }
    } catch (error) {
      console.error('Error fetching account:', error);
    }
  }, []);

  useEffect(() => {
    fetchAccount();
  }, []);

  return (
    <Stack w="100%" flexDir="column" p="40px 48px" gap="12px">
      <Text fontSize="28px" color="neutrals.900" fontWeight="bold">
        Interview Dashboard
      </Text>
      <Tabs.Root
        defaultValue="copilot"
        variant="plain"
        value={tab}
        onValueChange={(e) => {
          setTab(e.value);
          setSearchParams({ tab: e.value });
        }}
      >
        <Flex justify="space-between" align="center">
          <Tabs.List h="60px">
            <Tabs.Trigger
              value="automated"
              h="100%"
              border={tab === 'automated' ? '1px solid' : 'none'}
              borderBottom="none"
              borderColor="neutrals.200"
              borderTopRadius="8px"
              borderBottomRadius="0"
              fontSize="18px"
              fontWeight="bold"
              color={tab === 'automated' ? 'primary.900' : 'neutrals.700'}
              bg={tab === 'automated' ? 'background.secondary' : 'transparent'}
              px="20px"
              py="16px"
            >
              <Flex gap="10px" align="center">
                <Text>Automated</Text>
                <Badge
                  fontSize="16px"
                  w={automatedUnreadCount > 99 ? '36px' : '26px'}
                  h={automatedUnreadCount > 99 ? '28px' : '26px'}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  borderRadius="full"
                  bg="primary.900"
                  color="neutrals.0"
                >
                  {automatedUnreadCount > 99 ? '99+' : automatedUnreadCount}
                </Badge>
              </Flex>
            </Tabs.Trigger>
            <Tabs.Trigger
              value="copilot"
              h="100%"
              border={tab === 'copilot' ? '1px solid' : 'none'}
              borderBottom="none"
              borderColor="neutrals.200"
              borderTopRadius="8px"
              borderBottomRadius="0"
              fontSize="18px"
              fontWeight="bold"
              color={tab === 'copilot' ? 'primary.900' : 'neutrals.700'}
              bg={tab === 'copilot' ? 'background.secondary' : 'transparent'}
              px="20px"
              py="16px"
            >
              <Box display={{ base: tab === 'overview' ? 'block' : 'none', sm: 'block' }}>
                Copilot
              </Box>
            </Tabs.Trigger>
          </Tabs.List>
          {tab === 'copilot' ? (
            <CopilotActions
              onAddInterview={handleAddMeeting}
              onAddMockInterview={handleAddMockMeeting}
              agencyPlan={agencyPlan}
              isExhausted={usage?.exhausted || false}
            />
          ) : (
            <AutomatedActions />
          )}
        </Flex>

        <Tabs.Content value="automated" p="0">
          <AgentIndex
            setUnreadCount={(count) => {
              setAutomatedUnreadCount(count);
            }}
          />
        </Tabs.Content>

        <Tabs.Content value="copilot" p="0">
          <CopilotIndex key={copilotRefreshKey} />
        </Tabs.Content>
      </Tabs.Root>
    </Stack>
  );
}

interface CopilotActionsProps {
  onAddInterview: () => void;
  onAddMockInterview: () => void;
  agencyPlan: AgencyPlan;
  isExhausted: boolean;
}

const CopilotActions = ({
  onAddInterview,
  onAddMockInterview,
  agencyPlan,
  isExhausted
}: CopilotActionsProps) => {
  return (
    <Flex gap="16px">
      {(import.meta.env.VITE_APP_ENV === 'development' ||
        import.meta.env.VITE_APP_ENV === 'debug') && (
        <AddMockInterviewDialog onAddInterview={onAddMockInterview} />
      )}
      <AddInterviewDialog
        mode="add"
        onAddInterview={onAddInterview}
        plan={agencyPlan}
        isExhausted={isExhausted}
      />
    </Flex>
  );
};

const AutomatedActions = () => {
  const navigate = useNavigate();
  const [isLimitDialogOpen, setIsLimitDialogOpen] = useState(false);
  const [isFreePlan, setIsFreePlan] = useState(false);

  const handleAddSession = useCallback(async () => {
    // Fetch fresh account data to check latest limits
    try {
      const response = await fetchAPI<Account>({
        request: () => apiGetAccountProfile()
      });

      if (response.success && response.data) {
        const freshAccount = response.data;
        const agenticInterviewsUsage = freshAccount.agency?.usages?.find(
          (usage) => usage.type === AgencyUsageType.AgenticInterviews
        );
        const isExhausted = agenticInterviewsUsage?.exhausted || false;
        const isPlanFree = freshAccount.agency?.plan === AgencyPlan.FREE;

        if (isExhausted || isPlanFree) {
          setIsFreePlan(isPlanFree);
          setIsLimitDialogOpen(true);
        } else {
          navigate('/interview/agent/create-session');
        }
      } else {
        // If API call fails, allow navigation
        navigate('/interview/agent/create-session');
      }
    } catch {
      // If error occurs, allow navigation
      navigate('/interview/agent/create-session');
    }
  }, [navigate]);

  return (
    <>
      <Flex gap="16px">
        <Button
          variant="outline"
          border="1px solid"
          borderColor="primary.900"
          color="primary.900"
          borderRadius="8px"
          p="8px 16px"
          _hover={{ bg: 'primary.900', color: 'neutrals.0' }}
          onClick={() => navigate('/interview/question-bank')}
        >
          <Flex gap="4px" align="center">
            <Text fontSize="16px" fontWeight="semibold">
              Question Management
            </Text>
            <ListPlus size={20} weight="bold" />
          </Flex>
        </Button>
        <Button
          color="neutrals.0"
          bg="primary.900"
          borderRadius="8px"
          fontWeight="semibold"
          fontSize="14px"
          p="10px 24px"
          _hover={{ bg: 'primary.700' }}
          aria-label="Add a new session"
          onClick={handleAddSession}
        >
          Add a Session
          <Microphone size={24} aria-hidden="true" />
        </Button>
      </Flex>
      <LimitExhaustedDialog
        isOpen={isLimitDialogOpen}
        onClose={() => setIsLimitDialogOpen(false)}
        isFreePlan={isFreePlan}
        usageType="agentic-interview"
      />
    </>
  );
};
